import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core'
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid'
import { NzCardComponent } from 'ng-zorro-antd/card'
import { NzIconDirective } from 'ng-zorro-antd/icon'
import { ApiService } from '@/app/modules/dashboard/infra/api.service'
import {
  Card,
  DashboardData,
  DashboardResponse,
} from '@/app/modules/dashboard/core/types'
import { BarItemsComponent } from '@/features/dashboard/pages/items/bar-items/bar-items.component'
import { ColumnItemsComponent } from '@/features/dashboard/pages/items/collumn-items/column-items.component'
import { LineItemsComponent } from '@/features/dashboard/items/line-items/line-items.component'
import { ResizeService } from './resize.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { LoaderService } from '@/common/services/loader/loader.service'
import { TableComponent } from "../../components/table/table.component";

import { NzModalModule } from 'ng-zorro-antd/modal'
import { NzTableModule } from 'ng-zorro-antd/table'
import { NzButtonModule } from 'ng-zorro-antd/button'
import { NzSelectModule } from 'ng-zorro-antd/select'
import { FormsModule } from '@angular/forms'
import { ChecklistListModalComponent } from '../../components/checklist-list-modal/checklist-list-modal.component';
import { TransferTableComponent } from '@/features/dashboard/components/transfer-table/transfer-table.component';
import { MaintenanceChartComponent } from '@/features/dashboard/components/maintenance-chart/maintenance-chart.component';
import { VehicleStatusPanelComponent } from '@/features/vehicle-recurrence/components/status-panel/status-panel.component';

import { NzToolTipModule } from 'ng-zorro-antd/tooltip'

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    NzRowDirective,
    NzColDirective,
    NzCardComponent,
    NzIconDirective,
    BarItemsComponent,
    ColumnItemsComponent,
    LineItemsComponent,
    TableComponent,
    NzModalModule,
    NzTableModule,
    NzButtonModule,
    NzSelectModule,
    FormsModule,
    ChecklistListModalComponent,
    TransferTableComponent,
    MaintenanceChartComponent,
    VehicleStatusPanelComponent,
    NzToolTipModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(BarItemsComponent) barItemsComponent?: BarItemsComponent
  @ViewChild(ColumnItemsComponent) columnItemsComponent?: ColumnItemsComponent
  @ViewChild(LineItemsComponent) lineItemsComponent?: LineItemsComponent
  @ViewChild(MaintenanceChartComponent) maintenanceChartComponent?: MaintenanceChartComponent

  // ViewChildren para alcançar os componentes dentro dos hidden divs
  @ViewChildren(ChecklistListModalComponent) checklistLists?: QueryList<ChecklistListModalComponent>;
  @ViewChildren(TransferTableComponent) transferTables?: QueryList<TransferTableComponent>;
  @ViewChildren(TableComponent) userTables?: QueryList<TableComponent>;
  @ViewChildren(VehicleStatusPanelComponent) vehicleStatusPanels?: QueryList<VehicleStatusPanelComponent>;

  public maintenanceSelectedChassis: string | null = null;
  public cards: Card[] = [
    { name: 'Total', icon: 'check-circle', register: 0 },
    { name: 'Em Andamento', icon: 'warning', register: 0 },
    { name: 'Cancelados', icon: 'close-circle', register: 0 },
    { name: 'Envios', icon: 'arrows-alt', register: 0 },
    { name: 'Recebimentos', icon: 'shrink', register: 0 },
  ]
  public apiData: DashboardData[] = []
  private destroy$ = new Subject<void>()

  // Monitor Mode State
  public isMonitorMode = false;
  public currentStage = 0;
  public isPaused = false;
  private monitorInterval: any;
  private refreshInterval: any;

  // Modal State
  public isVisible = false;
  public modalTitle = '';
  public activeCardName = '';
  public modalFilters: { type?: string[], status?: string[] } = {};
  public modalData: DashboardData[] = [];

  constructor(
    private dashApiService: ApiService,
    private cdr: ChangeDetectorRef,
    private resizeService: ResizeService,
    private loaderService: LoaderService
  ) {
    this.resizeService.resize$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => {
          this.resizeAllCharts()
        }, 300)
      })
  }

  ngOnInit() {
    this.findAllDataToDashboard()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.resizeAllCharts()
    }, 300)
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.stopMonitorMode()
  }

  public toggleMonitorMode(): void {
    this.isMonitorMode = !this.isMonitorMode;
    if (this.isMonitorMode) {
      this.startMonitorMode();
    } else {
      this.stopMonitorMode();
    }
  }

  public togglePause(): void {
    this.isPaused = !this.isPaused;
    // Não paramos mais o monitor mode aqui, pois o monitorInterval 
    // já checa a flag isPaused internamente para não mudar o stage.
    // Isso garante que o refreshInterval continue rodando mesmo pausado.
    
    // Se acabamos de despausar, podemos querer resetar o timer do carousel 
    // para dar os 30s cheios a partir de agora.
    if (!this.isPaused) {
       this.resetCarouselTimer();
    }
  }

  private resetCarouselTimer(): void {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    this.monitorInterval = setInterval(() => {
      if (!this.isPaused) {
        this.nextStage();
      }
    }, 30000);
  }

  public goToStage(index: number): void {
    this.currentStage = index;
    this.cdr.detectChanges();
    setTimeout(() => this.resizeAllCharts(), 200);
    
    // Reinicia o timer para dar 30s a partir do clique manual
    this.resetCarouselTimer();
  }

  private startMonitorMode(): void {
    this.stopMonitorMode();
    
    // Carousel Interval
    this.monitorInterval = setInterval(() => {
      if (!this.isPaused) {
        this.nextStage();
      }
    }, 30000); // 30 segundos

    // Data Refresh Interval (Independente do Pause)
    this.refreshInterval = setInterval(() => {
      this.refreshMonitorData();
    }, 60000); // Atualiza dados a cada 60 segundos
    
    // Pequeno delay para os componentes renderizarem antes do primeiro resize
    setTimeout(() => this.resizeAllCharts(), 500);
  }

  private stopMonitorMode(): void {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  public refreshMonitorData(): void {
    // 1. Atualiza KPIS do Dashboard Principal
    this.findAllDataToDashboard();

    // 2. Atualiza Gráficos (Eles têm lógica de fetch interna)
    this.barItemsComponent?.findAllDataToDashboard();
    this.lineItemsComponent?.getApiChartData();
    this.columnItemsComponent?.getApiChartData();
    
    // Para o Maintenance, forçamos o refresh re-setando o valor do chassis
    const currentChassis = this.maintenanceSelectedChassis;
    this.maintenanceSelectedChassis = null;
    this.cdr.detectChanges();
    this.maintenanceSelectedChassis = currentChassis;

    // 3. Atualiza Tabelas e Listas usando os ViewChildren
    this.checklistLists?.forEach(list => list.loadData());
    this.transferTables?.forEach(table => table.loadData());
    this.userTables?.forEach(table => table.getApiData());
    this.vehicleStatusPanels?.forEach(panel => panel.loadData());
  }

  private nextStage(): void {
    this.currentStage = (this.currentStage + 1) % 6;
    this.cdr.detectChanges();
    
    // Garantir que os gráficos se ajustem ao novo slide
    setTimeout(() => this.resizeAllCharts(), 200);
  }

  public getStageName(stage: number): string {
    const names = [
      'Visão Geral e Gráficos',
      'Check-lists em Andamento',
      'Monitoramento de Transferências',
      'Status de Veículos (Em dia x Atrasados)',
      'Painel de Manutenção',
      'Equipe e Modelos'
    ];
    return names[stage] || '';
  }

  private resizeAllCharts(): void {
    // Força a detecção de mudanças para garantir que os ViewChilds estejam disponíveis
    this.cdr.detectChanges()

    // Chama o redimensionamento com um pequeno atraso
    this.barItemsComponent?.resizeChart()
    this.columnItemsComponent?.resizeChart()
    this.lineItemsComponent?.resizeChart()
  }

  findAllDataToDashboard(): void {
    this.loaderService
      .executeWithLoader(this.dashApiService.findAll())
      .subscribe({
        next: (dashData: DashboardResponse<DashboardData>) => {
          this.apiData = dashData.items
          this.updateCards()
          this.resizeAllCharts() // Redimensiona após receber os dados
        },
        error: (err: Error) => {
          err.message
        },
      })
  }

  setConfigsAtCards(card: Card): Card {
    switch (card.name) {
      case 'Total':
        return {
          ...card,
          icon: 'check-circle',
          register: this.calculateAllRegisters(),
          color: '#1890ff' // Blue
        }
      case 'Envios':
        return {
          ...card,
          icon: 'arrows-alt',
          register: this.calculateSend(),
          color: '#00869e' // Teal
        }
      case 'Cancelados':
        return {
          ...card,
          icon: 'close-circle',
          register: this.calculateCanceled(),
          color: '#c0142c' // Red
        }
      case 'Recebimentos':
        return {
          ...card,
          icon: 'shrink',
          register: this.calculateReceives(),
          color: '#7cb342' // Green
        }
      case 'Em Andamento':
        return {
          ...card,
          icon: 'warning',
          register: this.calculateInProgress(),
          color: '#ffad1f' // Orange
        }
      default:
        return { ...card, icon: 'warning', register: 0 }
    }
  }

  private calculateSend(): number {
    return this.apiData.filter((item) => item.type === 'transfer_start').length
  }

  private calculateCanceled(): number {
    return this.apiData.filter((item) => item.status === 'canceled').length
  }

  private calculateReceives(): number {
    return this.apiData.filter((item) => item.type === 'transfer_done').length
  }

  private calculateInProgress(): number {
    return this.apiData.filter((item) => item.status === 'in_progress').length
  }

  private calculateAllRegisters(): number {
    return this.apiData.filter((item) => item).length
  }

  updateCards(): void {
    this.cards = this.cards.map((card) => this.setConfigsAtCards(card))
  }

  /*
  @HostListener('document:visibilitychange')
  onVisibilityChange(event?: any): void {
    if (document.visibilityState === 'visible') {
      setTimeout(() => {
        this.resizeAllCharts()
      }, 200)
    }
  }
  */

  // Modal Logic
  public expandedChartType: string | null = null;

  public handleCardClick(card: Card): void {
    this.modalTitle = card.name;
    this.isVisible = true;
    this.expandedChartType = null; // Reset chart type when opening card modal
    this.activeCardName = card.name;

    switch (card.name) {
      case 'Total':
        this.modalFilters = {};
        break;
      case 'Envios':
        this.modalFilters = { type: ['transfer_start'] };
        break;
      case 'Cancelados':
        this.modalFilters = { status: ['canceled'] };
        break;
      case 'Recebimentos':
        this.modalFilters = { type: ['transfer_done'] };
        break;
      case 'Em Andamento':
        this.modalFilters = { status: ['in_progress'] };
        break;
      default:
        this.modalFilters = {};
    }
  }

  public openExpandedChart(type: string): void {
    this.expandedChartType = type;
    this.isVisible = true;
    this.activeCardName = ''; // Reset active card

    switch (type) {
      case 'month':
        this.modalTitle = 'Check-lists por Mês (Expandido)';
        break;
      case 'model':
        this.modalTitle = 'Check-lists por Modelo (Expandido)';
        break;
      case 'company':
        this.modalTitle = 'Check-lists por Empresas (Expandido)';
        break;
      case 'transfer':
        this.modalTitle = 'Monitoramento de Transferências (Chassi) - Expandido';
        break;
      case 'maintenance':
        this.modalTitle = 'Análise de Manutenção (Últimos 6 meses) - Expandido';
        break;
      case 'vehicle-status':
        this.modalTitle = 'Status dos Veículos (Em Dia x Atrasados) - Expandido';
        break;
    }
  }

  public handleCancel(): void {
    this.isVisible = false;
    this.expandedChartType = null;
    this.activeCardName = '';
  }
}
